// client/src/features/equipment/search.ts — SCR-EQP-001's URL state · MAINT-006 (04 §9.2.3).
//
// §9.2.3 is normative for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered register is shareable, survives refresh, and Back
// is a filter-undo. `features/repairs/search.ts` is the shape this copies.
//
// The names are `GET /api/v1/equipment`'s own — `propertyId`, `equipmentType`, `active`,
// `dueBefore`, `q` plus the 02 §7.4 paging — so the URL of the screen and the query string of
// the request are the same vocabulary and `toListQuery` is a rename-free pass-through.
//
// TWO PARAMS ARE NOT THE ENDPOINT'S.
//
//   • `view` — 05 MAINT-006 asks for a register AND an „upcoming-revisions calendar view", two
//     renderings of one set, and which one a user is looking at is exactly the state §9.2.3
//     wants in the URL: a colleague received the calendar, not the register.
//   • `highlight` — the deep-link target of 05 MAINT-006's criterion („completing a revision
//     from the task deep-link works end-to-end"). A task that points at the REGISTER rather
//     than at one equipment names the row it means, and the register scrolls to it and marks
//     it. It is not a filter: the row is shown in its place among the others, because a
//     landlord who followed „programează revizia" still wants to see what else is due that week.

import { z } from 'zod';

import { EQUIPMENT_TYPES } from './enums';

/** The two renderings of SCR-EQP-001. The register is the default: it answers „what do I have". */
export const EQUIPMENT_VIEWS = ['register', 'calendar'] as const;

export type EquipmentView = (typeof EQUIPMENT_VIEWS)[number];

/** How far the calendar looks ahead when the URL does not say — two quarters of scadențe. */
export const CALENDAR_HORIZON_DAYS = 180;

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const equipmentSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().optional(),
  q: z.string().trim().optional(),
  propertyId: z.string().uuid().optional(),
  equipmentType: z.enum(EQUIPMENT_TYPES).optional(),
  /**
   * „În funcțiune" / „Scoase din uz" / both — the endpoint's own tri-state.
   *
   * NOT `z.coerce.boolean()`. That is `Boolean(value)`, and `Boolean('false')` is `true`: a URL
   * saying `active=false` would ask the endpoint for the equipment still in service and show
   * a landlord the opposite of the filter they picked. Both spellings are accepted because both
   * reach this schema — the string when the URL is parsed, the real boolean when the toggle
   * navigates with an object — and both mean what they say.
   */
  active: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => (typeof value === 'boolean' ? value : value === 'true'))
    .optional(),
  /** Only scadențe on or before this date; the calendar's horizon lives here. */
  dueBefore: z.string().optional(),
  view: z.enum(EQUIPMENT_VIEWS).default('register'),
  /** The equipment a deep link named — scrolled to and marked, never filtered to. */
  highlight: z.string().uuid().optional(),
});

export type EquipmentSearch = z.infer<typeof equipmentSearchSchema>;

/**
 * Which filters are engaged — the empty state needs to know why it is empty. Neither `view` nor
 * `highlight` is one of them: switching to the calendar filters nothing out, and a highlight
 * that matched no row is still a register with everything in it.
 */
export function hasActiveFilters(search: EquipmentSearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.propertyId !== undefined ||
    search.equipmentType !== undefined ||
    search.active !== undefined ||
    search.dueBefore !== undefined
  );
}

/** Clearing filters returns to page 1 and keeps the rendering the user chose. */
export function clearedSearch(view: EquipmentView): EquipmentSearch {
  return { page: 1, pageSize: 25, view };
}
